import { useEffect, forwardRef } from 'react';

// styles
import styles from './showroom.module.scss';

// utils
import { classNameBuilder } from 'utils/classNameBuilder';

type GoogleMapProps = {
  coordinates: google.maps.LatLngLiteral | undefined;
  googleMapsRef: typeof window.google.maps | null;
  mapConfig: {
    maxZoom: number;
    mapStyles: google.maps.MapOptions;
  };
  gmap: google.maps.Map | null;
  setGmap: (gmap: google.maps.Map) => void;
  zoom?: number;
}

function GoogleMapWithoutRef(
  { coordinates, zoom, googleMapsRef, mapConfig, gmap, setGmap }: Readonly<GoogleMapProps>,
  mapRef: React.MutableRefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (googleMapsRef && mapRef.current && !gmap) {
      setGmap(
        new googleMapsRef.Map(mapRef.current, {
          maxZoom: mapConfig.maxZoom,
          ...mapConfig.mapStyles,
        }),
      );
    }
    return () => {
      if (googleMapsRef) {
        googleMapsRef.event.clearInstanceListeners(window);
        googleMapsRef?.event.clearInstanceListeners(document);
      }
    };
  }, [gmap, googleMapsRef, mapConfig.mapStyles, mapConfig.maxZoom, mapRef, setGmap]);

  useEffect(() => {
    if (gmap && coordinates) {
      gmap.setCenter(coordinates);
    }
  }, [coordinates, gmap]);

  useEffect(() => {
    if (gmap && zoom) {
      gmap.setZoom(zoom);
    }
  }, [zoom, gmap]);

  useEffect(() => {
    if (gmap) {
      if (coordinates) {
        gmap.panTo(coordinates);
      }
      if (zoom) {
        gmap.setZoom(zoom);
      }
    }
  }, [zoom, coordinates, gmap]);

  return <div className={classNameBuilder(styles.map)} ref={mapRef} />;
}

export const GoogleMap = forwardRef(GoogleMapWithoutRef);
